import {
  provideRouter,
  RouteReuseStrategy,
  ROUTES,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { WebNoopHttpInterceptor } from '@app/services/interceptors/web-noop-http-interceptor.service';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { effects, reducers } from '@app/store';
import {
  APP_ID,
  APP_INITIALIZER,
  importProvidersFrom,
  inject,
} from '@angular/core';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { StorageUtilLibModule } from '@b2b/storage-util-lib';
import { STORAGE_CONST } from '@app/constants/storage-constants';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { DefaultRouteReuseStrategy } from '@app/services/route-strategy/default-route-reuse-strategy';
import { CustomRouteReuseStrategy } from '@app/services/route-strategy/custom-route-reuse-strategy';
import { AppSettingsService } from '@app/services/global-services/app/app-settings.service';
import { catchError, of, tap } from 'rxjs';
import { AppVersionSetting } from '@app/models/server/app-version.model';
import { APP_BASE_HREF } from '@angular/common';
import { environment } from '@src/environments/environment';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { HttpLoaderInterceptor } from '@app/services/interceptors/loader-interceptor.service';
import { getWebRoutes } from './web-app-routing';
import { AnalyticsAdapter } from '@app/api-v2/util/analytics/analytics.adapter';
import { WebAnalyticsAdapter } from '@app/api-v2/util/analytics/web-analytics.adapter';
import { WebHttpClientService } from '@app/api-v2/util/http/web-http-client.service';
import { HttpClientService } from '@app/api-v2/util/http/http-client.service';

export function getWindow() {
  return typeof window !== 'undefined' ? window : null;
}

export function getNavigator() {
  return typeof navigator !== 'undefined' ? navigator : null;
}

const jwtOptionsFactory = (storage: any) => {
  return {
    tokenGetter: () => {
      if (storage) {
        return storage.get(STORAGE_CONST.AUTH_TOKEN);
      } else {
        return null;
      }
    },
  };
};

function initializeState(appSettingsService: AppSettingsService) {
  return () => {
    return appSettingsService.getAppVersion().pipe(
      tap((result: AppVersionSetting) => {
        AppSettingsService.appVersionSetting = result;
        appSettingsService.setAppCacheVersion(result);
        appSettingsService.verifyAppVersion(result);
        environment.t = result?.APP_SETTING_TITLE;
        environment.d = result?.APP_SETTING_DESCRIPTION;
        environment.m = result?.M_VERSION;
        environment.imgHost = result?.IMAGE_HOST
          ? result?.IMAGE_HOST
          : environment.imgHost;
      }),
      catchError((error) => {
        return of({});
      }),
    );
  };
}

export const GLOBAL_STATE_APP_INITIALIZER_PROVIDER = [
  {
    provide: APP_INITIALIZER,
    useFactory: initializeState,
    deps: [AppSettingsService],
    // useFactory: fixPageFlickering,
    // deps: [PLATFORM_ID],
    multi: true,
  },
];

export const WEB_APP_PROVIDERS = [
  {
    provide: APP_BASE_HREF,
    useValue: environment?.baseHref,
  },
  { provide: APP_ID, useValue: 'b2b-app' },
  importProvidersFrom([StorageUtilLibModule]),

  GLOBAL_STATE_APP_INITIALIZER_PROVIDER,
  provideRouter(
    [],
    /**
     * **🚀 Perf Tip for TBT:**
     *
     * Disable initial sync navigation in router config and schedule it in router-outlet container component
     */
    // withDisabledInitialNavigation(),
    withComponentInputBinding(),
    withEnabledBlockingInitialNavigation(),
    withInMemoryScrolling({
      /**
       * **💡 UX Tip for InfiniteScroll:**
       *
       * Reset scroll position to top on route change, users could be
       * irritated starting a new list from the bottom of the page.
       *
       * also: otherwise infinite scroll isn't working properly
       */
      scrollPositionRestoration: 'enabled',
    }),
  ),
  {
    provide: ROUTES,
    useFactory: () => {
      const b2bDeviceUtilService = inject(B2bDeviceUtilService);
      return getWebRoutes(b2bDeviceUtilService);
    },
    multi: true,
  },
  { provide: HttpClientService, useClass: WebHttpClientService },
  provideHttpClient(
    withFetch(),
    withInterceptors([HttpLoaderInterceptor, WebNoopHttpInterceptor]),
  ),
  provideClientHydration(
    withEventReplay(),
    withHttpTransferCacheOptions({
      includePostRequests: true,
      includeRequestsWithAuthHeaders: true,
      filter: (req) => true,
    }),
  ),
  provideAnimationsAsync(),
  provideStore(reducers, {
    runtimeChecks: {
      strictStateImmutability: true,
      strictActionImmutability: true,
    },
  }),
  provideEffects(effects),
  {
    provide: RouteReuseStrategy,
    useFactory: () => {
      const b2bDeviceUtilService = inject(B2bDeviceUtilService);
      if (b2bDeviceUtilService?.isDesktop()) {
        return new DefaultRouteReuseStrategy();
        // return null;
      } else {
        return new CustomRouteReuseStrategy();
        // return new IonicRouteStrategy();
      }
      // return getWebRoutes(b2bDeviceUtilService);
    },
    // useClass: CustomRouteReuseStrategy,
  },
  provideIonicAngular({
    backButtonText: '',
    backButtonIcon: 'arrow-back',
  }),
  { provide: 'WINDOW', useFactory: getWindow },
  { provide: 'NAVIGATOR', useFactory: getNavigator },
  { provide: AnalyticsAdapter, useClass: WebAnalyticsAdapter },
];
